export interface Feature {
  emoji: string
  title: string
  body: string
}

export const features: Feature[] = [
  {
    emoji: '🔩',
    title: '3000+ Fabrication Plans',
    body: 'Access a massive organized collection of practical metal fabrication projects covering different types of workshop and custom builds.',
  },
  {
    emoji: '📐',
    title: 'Ready Measurements',
    body: 'Use dimensional references and project layouts to reduce the time normally spent designing everything from zero.',
  },
  {
    emoji: '🏗️',
    title: 'Real-World Project Ideas',
    body: 'Explore gates, furniture, workshop builds, storage, railings, outdoor projects, utility builds and much more.',
  },
  {
    emoji: '🛠️',
    title: 'Practical Build References',
    body: 'Use clear visual project references to understand the structure, proportions and construction of different builds.',
  },
  {
    emoji: '⚡',
    title: 'Faster Project Planning',
    body: 'Go from “What should I build?” to an actionable project direction without spending hours searching and sketching.',
  },
  {
    emoji: '💻',
    title: 'Instant Digital Access',
    body: 'Keep the entire collection together and access your project files whenever you need them from compatible digital devices.',
  },
]

export interface Bonus {
  title: string
  body: string
  close: string
}

export const bonuses: Bonus[] = [
  {
    title: 'Bonus #1 — Workshop Measurement & Conversion Toolkit™',
    body: 'Get useful measurement references, dimensional conversions and workshop reference tables you can keep close while planning and building.',
    close: 'Measure faster. Check faster. Keep moving.',
  },
  {
    title: 'Bonus #2 — Smart Material Planning System™',
    body: 'Organize the materials, quantities, cuts and purchasing requirements for upcoming fabrication projects before the work begins.',
    close: 'Plan the material before cutting the metal.',
  },
  {
    title: 'Bonus #3 — Fabrication Project Checklist Pack™',
    body: 'Follow practical checklists covering project preparation, materials, cutting, assembly, welding and final project review.',
    close: 'A clearer workflow from start to finish.',
  },
  {
    title: 'Bonus #4 — Build & Quote Calculator™',
    body: 'Organize material costs, estimated labor time and basic project expenses to create a clearer reference when planning or quoting fabrication work.',
    close: 'Build with the numbers in front of you.',
  },
  {
    title: 'Bonus #5 — 50 High-Demand Metal Project Ideas™',
    body: 'Discover additional project concepts across furniture, workshop, outdoor, storage and practical fabrication categories. Use them for inspiration when choosing what to build, customize or offer next.',
    close: 'Never stare at a blank project list again.',
  },
]

export const differentiators: Feature[] = [
  {
    emoji: '⏱️',
    title: 'Save Planning Time',
    body: 'Start from an existing project reference instead of a blank page.',
  },
  {
    emoji: '💡',
    title: 'Find More Ideas',
    body: 'Explore thousands of fabrication concepts without constantly searching online.',
  },
  {
    emoji: '🔩',
    title: 'Build More Efficiently',
    body: 'Spend less time figuring out what to make and more time actually fabricating.',
  },
  {
    emoji: '🛠️',
    title: 'Create a Better Workflow',
    body: 'Keep plans, references and project ideas organized in one complete collection.',
  },
]

export interface Step {
  number: string
  title: string
  body: string
}

export const steps: Step[] = [
  {
    number: '01',
    title: 'Complete Your Purchase',
    body: 'Get METAL FABRICATION READY™ through our secure checkout.',
  },
  {
    number: '02',
    title: 'Get Instant Access',
    body: 'Receive immediate digital access to your complete collection.',
  },
  {
    number: '03',
    title: 'Open It Anywhere',
    body: 'Browse your fabrication plans and resources from your compatible digital devices.',
  },
  {
    number: '04',
    title: 'Choose & Start Building',
    body: 'Find a project, review the references, prepare your materials and get to work.',
  },
]

export const audience: Feature[] = [
  { emoji: '🔩', title: 'Metal Fabricators', body: 'Practical references for everyday fabrication work.' },
  { emoji: '🛠️', title: 'Workshop Owners', body: 'A large project library ready whenever a new build is needed.' },
  { emoji: '🏗️', title: 'Custom Project Builders', body: 'More starting points for custom fabrication projects.' },
  { emoji: '⚙️', title: 'DIY Metal Builders', body: 'Projects and references for hands-on personal builds.' },
  { emoji: '📐', title: 'Developing Fabricators', body: 'A broad collection for exploring different types of projects.' },
  {
    emoji: '🔥',
    title: 'Experienced Professionals',
    body: 'Save planning time and expand the range of project ideas available.',
  },
]

export const offerIncludes: string[] = [
  '3000+ Metal Fabrication Plans',
  'Ready Measurements & References',
  'Multiple Project Categories',
  'Practical Build Ideas',
  'Instant Digital Access',
  '5 Exclusive Bonuses',
]

export interface Testimonial {
  quote: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'I used to spend more time looking for ideas than actually working on them. Now I can open the collection, find something useful and get moving.',
    name: 'Michael R.',
    role: 'Fabricator, 48 — Ohio, United States',
  },
  {
    quote:
      "There are projects in here I wouldn't have thought of building myself. Having everything organized in one place makes a huge difference.",
    name: 'James T.',
    role: 'Workshop owner, 52 — Leeds, United Kingdom',
  },
  {
    quote:
      'The biggest benefit for me is having a starting point. Instead of beginning every project with an empty page, I already have something to work from.',
    name: 'Robert D.',
    role: 'Custom builder, 41 — Alberta, Canada',
  },
  {
    quote:
      'Most of my week used to disappear into sketching gates and railings before a single cut. Now I pull a plan that is close to what the customer wants, adjust the dimensions to the opening and take the material list straight to the supplier. The quote calculator alone has changed how I price jobs.',
    name: 'Andrew P.',
    role: 'Gate & railing fabricator, 45 — Brisbane, Australia',
  },
  {
    quote:
      'I weld as a hobby in a two-car garage, so I never really knew what was realistic to attempt next. Going through the workshop and storage categories gave me a whole year of builds — benches, shelving, a welding table — with proportions that actually make sense instead of guesswork.',
    name: 'Steve C.',
    role: 'DIY metal builder, 37 — Manchester, United Kingdom',
  },
]

export interface Faq {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  { question: 'Is this a physical product?', answer: 'No. METAL FABRICATION READY™ is a 100% digital collection.' },
  {
    question: 'What exactly do I receive?',
    answer:
      'You receive access to a collection of 3000+ metal fabrication plans and project references, plus the five included bonuses.',
  },
  { question: 'How do I receive access?', answer: "You'll receive digital access after completing your purchase." },
  {
    question: 'Can I access it from different devices?',
    answer: 'Yes. The digital resources can be accessed from compatible devices.',
  },
  {
    question: 'Do I need to be an experienced metal fabricator?',
    answer:
      'No. The collection can provide useful project references for different experience levels. You should always work within your own skills and follow appropriate fabrication and workshop safety practices.',
  },
  {
    question: 'What types of projects are included?',
    answer:
      'The collection covers multiple fabrication categories, including workshop projects, furniture, gates, storage, outdoor builds, railings, utility projects and more.',
  },
  {
    question: 'Are measurements included?',
    answer: 'The collection includes dimensional references where provided for the applicable project.',
  },
  {
    question: 'Can I modify the projects?',
    answer:
      'Yes. Plans can serve as starting references that you adapt according to your materials, dimensions, requirements and experience.',
  },
  { question: 'Is this a subscription?', answer: 'No. This is a one-time purchase.' },
  {
    question: 'How quickly can I start?',
    answer: 'You receive digital access after purchase, so you can begin exploring the collection immediately.',
  },
]
